import { PlayerError } from '@foundation-player/error-codes';
import { getLogger } from '@foundation-player/logger';
import { ManifestPriority } from '@foundation-player/runtime';
import type { Manifest, Source } from '@foundation-player/types';
import { PlayerEvent } from '../event-emitter/events';
import { FoundationErrorEvent } from '../event-emitter/unfiltered';

/**
 * Determine the correct source according to provided manifest.
 *
 * @remarks
 * The resolver will receive a manifest which contains a list of sources.
 */
export class SourceResolver {
  private readonly logger = getLogger('player.core.manifest.SourceResolver');

  /**
   * Determine the source to use according to the provided manifest.
   *
   * @remarks
   * The method will just sanity check the manifest and sources, but otherwise return the list unchanged.
   *
   * @param manifest - The manifest to analyse
   */
  resolve(manifest: Manifest): Source {
    let source = manifest.sources.find((s) => s.priority === ManifestPriority.Main);
    this.logger.trace(`Try main source for manifest`, {
      manifest,
      source,
    });

    // if no main source was found take the first fallback source
    if (!source) {
      source = manifest.sources.find((s) => s.priority === ManifestPriority.Fallback);
      this.logger.trace(`Try fallback source for manifest`, {
        manifest,
        source,
      });
    }

    if (!source) {
      throw new FoundationErrorEvent(PlayerEvent.Error, {
        message: 'Could not determine a suitable source from manifest!',
        code: PlayerError.NO_MANIFEST_IN_CONFIG_34210.code,
        context: {
          manifest,
        },
      });
    }

    if (!source.url) {
      throw new FoundationErrorEvent(PlayerEvent.Error, {
        message: 'Empty manifest given!',
        code: PlayerError.VIDEOSOURCES_CONFIG_34200.code,
      });
    }

    return source;
  }
}
